package org.teddapp.project.ui.nav

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.APP_VERSION
import org.teddapp.project.ui.LocalAppSettings
import org.teddapp.project.ui.LocalTeddColors
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddKbd
import org.teddapp.project.ui.TeddSize
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.adaptive.LocalWindowClass
import org.teddapp.project.ui.adaptive.WindowClass
import org.teddapp.project.ui.teddCardShadow
import org.teddapp.project.ui.teddClickable

/**
 * Sections principales de l'app TEDD. Mappent au nav rail.
 * Pas toutes implémentées en V1 — cliquer sur une section non implémentée est no-op.
 */
/** Initiales pour le pastille avatar — 1 lettre si un seul mot, 2 si plusieurs. */
internal fun userInitials(name: String): String {
    val words = name.trim().split(Regex("\\s+")).filter { it.isNotBlank() }
    return when {
        words.isEmpty() -> "?"
        words.size == 1 -> words[0].take(1).uppercase()
        else -> (words[0].take(1) + words[1].take(1)).uppercase()
    }
}

enum class Section(val icon: String, val label: String) {
    Today("today", "Aujourd'hui"),
    Focus("focus", "Focus"),
    /** Portfolio & recueil de compétences : dans le rail, hors barre du bas (six places). */
    Portfolio("portfolio", "Portfolio"),
    Objectives("goals", "Objectifs"),
    Habits("habits", "Habitudes"),
    Finance("finance", "Finance"),
    Notes("notes", "Notes"),
    /** Cartes heuristiques : dans la barre latérale, derrière « Plus » sur téléphone. */
    MindMaps("mindmap", "Cartes heuristiques"),
    /** Trousseau de secrets : dans la barre latérale, hors barre du bas. */
    Vault("lock", "Trousseau"),
    /** Carte de visite à partager : dans la barre latérale, hors barre du bas. */
    Card("idcard", "Carte de visite"),
    /** Second onglet d'Objectifs : absent de la navigation principale. */
    Projects("grid", "Projets"),
    /** Troisième onglet d'Objectifs : absent de la navigation principale. */
    Skills("trend", "Compétences"),
    /** Back-office du site : n'apparaît dans la barre latérale que pour le `supra_user`. */
    Admin("globe", "Back-office"),
    /** Atteint depuis les réglages : absent de la navigation principale. */
    Backup("settings", "Sync");

    /** La section mise en évidence dans la navigation quand celle-ci est active. */
    val navHome: Section get() = if (this == Projects || this == Skills) Objectives else this
}

/** Les sections du rail (grand écran), dans l'ordre du parcours v2. */
val NAV_SECTIONS: List<Section> = listOf(
    Section.Today,
    Section.Focus,
    Section.Portfolio,
    Section.Objectives,
    Section.Notes,
    Section.MindMaps,
    Section.Habits,
    Section.Finance,
    Section.Vault,
    Section.Card,
)

/**
 * Les six piliers de la barre du bas (téléphone). Sept libellés ne tiennent pas sur
 * 360 dp ; Portfolio s'atteint depuis les réglages.
 */
val BOTTOM_NAV_SECTIONS: List<Section> = NAV_SECTIONS.filter { it != Section.Portfolio && it != Section.MindMaps && it != Section.Vault && it != Section.Card }

/** Couleur de repère d'un module : elle ne teinte que son icône et sa pastille. */
val Section.moduleColor: Color
    @Composable @ReadOnlyComposable
    get() = LocalTeddColors.current.let { c ->
        when (this) {
            Section.Today -> c.modToday
            Section.Focus -> c.modFocus
            Section.Portfolio -> c.modPortfolio
            Section.Objectives, Section.Projects, Section.Skills -> c.modGoals
            Section.Notes, Section.MindMaps -> c.modNotes
            Section.Habits -> c.modHabits
            Section.Finance -> c.modFinance
            Section.Admin -> c.laterite
            Section.Vault, Section.Card, Section.Backup -> c.inkMuted
        }
    }

/**
 * Ouvre la palette de commandes. Fourni par la plateforme qui en a une (le bureau) ;
 * absent ailleurs, et la barre latérale n'affiche alors pas le champ de recherche.
 */
val LocalOpenCommandPalette = staticCompositionLocalOf<(() -> Unit)?> { null }

/**
 * Navigation des mises en page larges.
 *
 * Sur ordinateur ([WindowClass.Expanded]) : la barre latérale du design system, 248 dp sur
 * la surface en retrait — marque, recherche, un élément par module, puis réglages et profil.
 * Sur une fenêtre moyenne : le même contenu réduit aux icônes.
 *
 * Le nom est resté celui du rail d'origine : chaque écran l'appelle déjà.
 */
@Composable
fun TeddNavRail(
    active: Section,
    onNavigate: (Section) -> Unit,
    onSettingsClick: () -> Unit,
    enabled: Set<Section> = Section.entries.toSet(),
) {
    // Sur ordinateur, la barre se règle à la poignée ; trop réduite, elle garde ses icônes.
    // Sur une fenêtre moyenne, elle reste aux icônes, sans poignée.
    if (LocalWindowClass.current == WindowClass.Expanded) {
        org.teddapp.project.ui.TeddSidePane(
            spec = NAV_PANE,
            title = "la navigation",
            contentPadding = androidx.compose.foundation.layout.PaddingValues(0.dp),
            collapsedContent = { expand -> NavRailBody(active, onNavigate, onSettingsClick, enabled, wide = false, onExpand = expand) },
        ) {
            NavRailBody(active, onNavigate, onSettingsClick, enabled, wide = true)
        }
    } else {
        Box(Modifier.width(64.dp).fillMaxHeight()) {
            NavRailBody(active, onNavigate, onSettingsClick, enabled, wide = false)
        }
    }
}

/** La barre de navigation : 248 dp par défaut, de 200 à 320 ; réduite, 64 dp d'icônes. */
private val NAV_PANE = org.teddapp.project.ui.PaneSpec("nav", default = TeddSize.Sidebar, min = 200.dp, max = 320.dp, collapsed = 64.dp, collapseBelow = 150.dp)

@Composable
private fun NavRailBody(
    active: Section,
    onNavigate: (Section) -> Unit,
    onSettingsClick: () -> Unit,
    enabled: Set<Section>,
    wide: Boolean,
    /** Réduite à la poignée : de quoi la rouvrir. */
    onExpand: (() -> Unit)? = null,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(TeddTokens.SurfaceSunken)
            .padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S4),
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S4),
        horizontalAlignment = if (wide) Alignment.Start else Alignment.CenterHorizontally,
    ) {
        TeddBrand(wide)
        val openPalette = LocalOpenCommandPalette.current
        if (wide && openPalette != null) SidebarSearch(openPalette)
        Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
            NAV_SECTIONS.forEach { section ->
                TeddNavItem(
                    label = section.label,
                    icon = section.icon,
                    iconColor = section.moduleColor,
                    active = section == active.navHome,
                    enabled = section in enabled,
                    wide = wide,
                    onClick = { if (section != active.navHome) onNavigate(section) },
                )
            }
            // Le back-office du site : seul le supra_user le voit. Le serveur revérifie le rôle.
            if (org.teddapp.project.server.LocalServerSession.current?.isSupra == true) {
                TeddNavItem(
                    label = Section.Admin.label,
                    icon = Section.Admin.icon,
                    iconColor = Section.Admin.moduleColor,
                    active = active == Section.Admin,
                    wide = wide,
                    onClick = { if (active != Section.Admin) onNavigate(Section.Admin) },
                )
            }
        }
        Spacer(Modifier.weight(1f))
        onExpand?.let {
            TeddNavItem(
                label = "Agrandir",
                icon = "chevron-right",
                iconColor = TeddTokens.InkMuted,
                active = false,
                wide = false,
                onClick = it,
            )
        }
        TeddNavItem(
            label = "Réglages",
            icon = "settings",
            iconColor = TeddTokens.InkMuted,
            active = false,
            wide = wide,
            onClick = onSettingsClick,
        )
        SidebarProfile(wide, onSettingsClick)
    }
}

/** La marque : le logo, suivi de « tedd » en minuscules quand la place le permet. */
@Composable
private fun TeddBrand(wide: Boolean) {
    Row(
        modifier = Modifier.height(36.dp).padding(horizontal = if (wide) 8.dp else 0.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        org.teddapp.project.ui.TeddLogo(size = 26.dp)
        if (wide) {
            Text(
                text = "teddApp",
                color = TeddTokens.Ink,
                style = TeddType.Title2.copy(fontSize = 22.sp, fontWeight = FontWeight(700), letterSpacing = (-0.9).sp),
            )
        }
    }
}

@Composable
private fun SidebarSearch(onClick: () -> Unit) {
    val shape = RoundedCornerShape(TeddTokens.RSm)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .teddClickable(onClick = onClick, shape = shape, fill = TeddTokens.Surface, hover = TeddTokens.Surface)
            .border(1.dp, TeddTokens.Line, shape)
            .height(36.dp)
            .padding(horizontal = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        TeddIcon(name = "search", size = 16.dp, color = TeddTokens.InkSubtle)
        Text("Rechercher", Modifier.weight(1f), color = TeddTokens.InkSubtle, style = TeddType.Label.copy(fontWeight = FontWeight(400)))
        TeddKbd("Ctrl K")
    }
}

/**
 * Élément de navigation : l'icône du module dans sa couleur de repère, son nom, et un
 * compteur facultatif. L'élément actif se pose sur la surface claire ; son compteur
 * passe sur citron vert.
 */
@Composable
fun TeddNavItem(
    label: String,
    icon: String,
    iconColor: Color,
    active: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    wide: Boolean = true,
    count: Int? = null,
) {
    val shape = RoundedCornerShape(TeddTokens.RSm)
    Row(
        modifier = modifier
            .then(if (wide) Modifier.fillMaxWidth() else Modifier.size(40.dp))
            .then(if (active) Modifier.teddCardShadow(shape) else Modifier)
            .teddClickable(
                onClick = onClick,
                shape = shape,
                fill = if (active) TeddTokens.Surface else Color.Transparent,
                hover = TeddTokens.Surface,
                enabled = enabled,
            )
            .height(if (wide) 36.dp else 40.dp)
            .padding(horizontal = if (wide) 10.dp else 0.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = if (wide) Arrangement.spacedBy(10.dp) else Arrangement.Center,
    ) {
        TeddIcon(name = icon, size = 18.dp, color = iconColor)
        if (wide) {
            Text(
                text = label,
                modifier = Modifier.weight(1f),
                color = if (active) TeddTokens.Ink else TeddTokens.InkMuted,
                style = TeddType.Label.copy(fontSize = 14.sp, fontWeight = FontWeight(if (active) 540 else 400)),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            if (count != null && count > 0) {
                Text(
                    text = count.toString(),
                    modifier = if (active) {
                        Modifier.background(TeddTokens.Lime, CircleShape).padding(horizontal = 6.dp, vertical = 1.dp)
                    } else Modifier,
                    color = if (active) TeddTokens.OnLime else TeddTokens.InkMuted,
                    style = TeddType.MonoSm.copy(fontSize = 11.sp),
                )
            }
        }
    }
}

@Composable
private fun SidebarProfile(wide: Boolean, onClick: () -> Unit) {
    val name = LocalAppSettings.current.userName
    Row(
        modifier = Modifier
            .then(if (wide) Modifier.fillMaxWidth() else Modifier)
            .teddClickable(onClick = onClick, hover = TeddTokens.Surface)
            .padding(horizontal = if (wide) 8.dp else 4.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Box(
            modifier = Modifier.size(28.dp).background(TeddTokens.Ink, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            Text(userInitials(name), color = TeddTokens.Bg, style = TeddType.Caption.copy(fontWeight = FontWeight(600)))
        }
        if (wide) {
            Column(Modifier.weight(1f)) {
                Text(
                    text = name.ifBlank { "Profil" },
                    color = TeddTokens.Ink,
                    style = TeddType.Label,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text("v$APP_VERSION", color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp))
            }
        }
    }
}
